import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { map } from "lodash";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Formik, Form, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import RsAdNewV2 from "components/buttons/Adnew-v2";
import SaveButton from "components/buttons/save";
import SafeField from "helpers/SafeField";
import { get } from "helpers/http_helper";
import {
  getActionPlanCategories,
  getActionPlans,
  getActionPlanDetail,
  clearActionPlanDetail,
  addUpdateActionPlan,
  deleteActionPlan,
} from "store/actions";
import "./actionPlans.css";

const ADMIN_AGENT_ID = 0;

const APPLIES_TO_OPTIONS = [
  { value: 1, label: "Lead" },
  { value: 2, label: "Client" },
];

const STEP_TYPE_OPTIONS = [
  { value: 1, label: "Task" },
  { value: 2, label: "Send automated email" },
];

const TASK_TYPE_OPTIONS = [
  { value: 1, label: "Send email" },
  { value: 2, label: "Send SMS" },
  { value: 3, label: "Call" },
  { value: 4, label: "Next Follow-up" },
];

const emptyStep = () => ({
  id: 0,
  actionStepType: 1,
  taskType: "",
  taskName: "",
  emailTemplate: "",
  daysAfterPreviousTask: 0,
});

const normalizeList = payload => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.resultData)) return payload.resultData;
  return [];
};

const ActionPlanList = props => {
  document.title = "Action Plans | Realty Space";

  const dispatch = useDispatch();
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { categories, plans, planDetail, loading } = useSelector(state => ({
    categories: state.ActionPlanReducer.categories,
    plans: state.ActionPlanReducer.plans,
    planDetail: state.ActionPlanReducer.planDetail,
    loading: state.ActionPlanReducer.loading,
  }));

  const [isRight, setIsRight] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [pauseOnRespond, setPauseOnRespond] = useState(true);

  const parsePauseOnRespond = value =>
    value === true || value === 1 || value === "1";

  const fetchEmailTemplates = (actionPlanType = 1) => {
    get("AgentSystemNotificationTemplate/bycategory", {
      params: { agentId: ADMIN_AGENT_ID, category: actionPlanType },
    })
      .then(res => setEmailTemplates(normalizeList(res)))
      .catch(() => setEmailTemplates([]));
  };

  useEffect(() => {
    dispatch(getActionPlanCategories(ADMIN_AGENT_ID));
    dispatch(getActionPlans(ADMIN_AGENT_ID, categoryId));
  }, [dispatch, categoryId]);

  useEffect(() => {
    if (isRight && isEdit && planDetail) {
      setPauseOnRespond(
        parsePauseOnRespond(
          planDetail.pauseOnRespond ?? planDetail.PauseOnRespond
        )
      );
      fetchEmailTemplates(
        planDetail.actionPlanType ?? planDetail.ActionPlanType ?? 1
      );
    }
  }, [isRight, isEdit, planDetail]);

  useEffect(() => {
    if (searchParams.get("add") === "1") {
      openAddPlan();
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const currentFolder = useMemo(
    () =>
      (categories || []).find(
        c =>
          String(c.id ?? c.Id) === String(categoryId) ||
          String(c.actionPlanCategoryId) === String(categoryId)
      ),
    [categories, categoryId]
  );

  const folderName =
    currentFolder?.actionPlanCategoryTitle ??
    currentFolder?.ActionPlanCategoryTitle ??
    props.t("Action plans");

  // Support flat plan list, or nested { actionPlans: [...] } if store/API shape varies
  const planRows = useMemo(() => {
    const raw = plans || [];
    const list =
      raw.length && Array.isArray(raw[0]?.actionPlans)
        ? raw[0].actionPlans
        : raw;
    return list.map(plan => ({
      ...plan,
      id: plan.id ?? plan.Id,
      actionPlanTitle: plan.actionPlanTitle ?? plan.ActionPlanTitle,
      actionPlanType: plan.actionPlanType ?? plan.ActionPlanType,
    }));
  }, [plans]);

  const toggle = () => setIsRight(!isRight);

  const openAddPlan = e => {
    if (e?.preventDefault) e.preventDefault();
    dispatch(clearActionPlanDetail());
    setEditingPlanId(null);
    setIsEdit(false);
    setPauseOnRespond(true);
    setIsRight(true);
    fetchEmailTemplates(1);
  };

  const openEditPlan = plan => {
    const id = plan.id ?? plan.Id;
    setEditingPlanId(id);
    setIsEdit(true);
    setPauseOnRespond(
      parsePauseOnRespond(plan.pauseOnRespond ?? plan.PauseOnRespond)
    );
    dispatch(getActionPlanDetail(id));
    setIsRight(true);
    fetchEmailTemplates(plan.actionPlanType ?? plan.ActionPlanType ?? 1);
  };

  const onClickDelete = plan => {
    setSelectedPlan(plan);
    setDeleteModal(true);
  };

  const handleDeletePlan = () => {
    if (selectedPlan?.id !== undefined) {
      dispatch(deleteActionPlan(selectedPlan));
      setDeleteModal(false);
    }
  };

  const initialValues = useMemo(() => {
    if (isEdit && planDetail) {
      const steps = planDetail.steps || planDetail.Steps || [];
      return {
        actionPlanTitle:
          planDetail.actionPlanTitle ?? planDetail.ActionPlanTitle ?? "",
        actionPlanCategory: String(
          planDetail.actionPlanCategory ??
            planDetail.ActionPlanCategory ??
            categoryId ??
            ""
        ),
        actionPlanType:
          planDetail.actionPlanType ?? planDetail.ActionPlanType ?? 1,
        steps: steps.length
          ? steps.map(s => {
              const stepType = s.actionStepType ?? s.ActionStepType ?? 1;
              const refId = s.taskTypeRefId ?? s.TaskTypeRefId;
              return {
                id: s.id ?? s.Id ?? 0,
                actionStepType: stepType,
                taskType: s.taskType ?? s.TaskType ?? "",
                taskName: s.taskName ?? s.TaskName ?? "",
                emailTemplate: refId
                  ? String(refId)
                  : s.emailTemplate ?? s.EmailTemplate ?? "",
                daysAfterPreviousTask:
                  s.daysAfterPreviousTask ?? s.DaysAfterPreviousTask ?? 0,
              };
            })
          : [emptyStep()],
      };
    }
    return {
      actionPlanTitle: "",
      actionPlanCategory: String(categoryId ?? ""),
      actionPlanType: 1,
      steps: [emptyStep()],
    };
  }, [isEdit, planDetail, categoryId]);

  const handlePlanSubmit = values => {
    const payload = {
      id: isEdit ? editingPlanId : 0,
      agentId: ADMIN_AGENT_ID,
      actionPlanCategory: parseInt(values.actionPlanCategory, 10),
      actionPlanType: parseInt(values.actionPlanType, 10),
      actionPlanTitle: values.actionPlanTitle,
      pauseOnRespond: pauseOnRespond ? 1 : 0,
      isActive: true,
      actionPlanSteps: values.steps.map((s, index) => {
        const actionStepType = parseInt(s.actionStepType, 10) || 1;
        return {
          id: s.id || 0,
          agentId: ADMIN_AGENT_ID,
          actionPlanId: isEdit ? editingPlanId : 0,
          actionStepType,
          // Automated email steps use taskType 1 (email) + template ref
          taskType:
            actionStepType === 2
              ? 1
              : parseInt(s.taskType, 10) || 1,
          taskName: s.taskName || "",
          taskTypeRefId: s.emailTemplate
            ? parseInt(s.emailTemplate, 10)
            : 0,
          emailTemplate: s.emailTemplate || "",
          taskBy: "Agent",
          stepOrder: index + 1,
          daysAfterPreviousTask: parseInt(s.daysAfterPreviousTask, 10) || 0,
          taskById: 0,
          isActive: true,
        };
      }),
    };

    dispatch(
      addUpdateActionPlan(payload, ok => {
        if (ok) {
          toggle();
          dispatch(getActionPlans(ADMIN_AGENT_ID, categoryId));
        }
      })
    );
  };

  return (
    <>
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeletePlan}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={props.t("Settings")}
                breadcrumbItem={folderName}
              />
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <div className="ap-page-header mb-3">
                <span className="ap-icon-badge">
                  <i className="bx bx-list-check" />
                </span>
                <div className="ap-title-block">
                  <h4>
                    {folderName}
                    <span className="ap-count-pill">
                      {planRows.length} {props.t("plans")}
                    </span>
                  </h4>
                  <p>
                    {props.t(
                      "Build a sequence of automated steps that run for every lead or client added to this folder."
                    )}
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <Card className="ap-card mt-2">
                <CardBody>
                  <div className="ap-toolbar">
                    <Button
                      tag={Link}
                      to="/action-plans"
                      color=""
                      size="sm"
                      className="ap-back-link"
                    >
                      <i className="bx bx-arrow-back" />
                      {props.t("Back")}
                    </Button>
                    <RsAdNewV2 to="#" onClick={openAddPlan}>
                      {props.t("Add new action plan")}
                    </RsAdNewV2>
                  </div>

                  <div className="table-responsive ap-datatable-wrap">
                    <DataTable
                      className="table table-bordered data-table align-middle table-nowrap mb-0"
                      value={planRows}
                      loading={loading}
                      paginator
                      rows={10}
                      rowsPerPageOptions={[10, 20, 30, 50]}
                      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                      emptyMessage={
                        <div className="ap-empty-state">
                          <i className="bx bx-list-plus" />
                          {props.t("No action plans found in this folder")}
                        </div>
                      }
                    >
                      <Column
                        field="actionPlanTitle"
                        header={props.t("Name")}
                        sortable
                        body={rowData => (
                          <Link
                            to="#"
                            className="text-primary"
                            onClick={e => {
                              e.preventDefault();
                              openEditPlan(rowData);
                            }}
                          >
                            {rowData.actionPlanTitle || "Untitled"}
                          </Link>
                        )}
                      />
                      <Column
                        field="actionPlanType"
                        header={props.t("Applies to")}
                        body={rowData => {
                          const isLead = rowData.actionPlanType === 1;
                          const isClient = rowData.actionPlanType === 2;
                          return (
                            <span
                              className={
                                "ap-badge " +
                                (isLead
                                  ? "ap-badge-lead"
                                  : isClient
                                    ? "ap-badge-client"
                                    : "ap-badge-na")
                              }
                            >
                              {isLead ? "Lead" : isClient ? "Client" : "N/A"}
                            </span>
                          );
                        }}
                      />
                      <Column
                        header={props.t("Actions")}
                        body={rowData => (
                          <div className="ap-actions-cell">
                            <ActionEdit
                              to="#"
                              onClick={e => {
                                e.preventDefault();
                                openEditPlan(rowData);
                              }}
                            />
                            <ActionDelete
                              to="#"
                              onClick={e => {
                                e.preventDefault();
                                onClickDelete(rowData);
                              }}
                            />
                          </div>
                        )}
                      />
                    </DataTable>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Offcanvas
            isOpen={isRight}
            direction="end"
            toggle={toggle}
            style={{ width: "min(920px, 100vw)" }}
          >
            <OffcanvasHeader toggle={toggle} className="ap-offcanvas-header">
              {isEdit
                ? props.t("Edit action plan")
                : props.t("Add new action plan")}
            </OffcanvasHeader>
            <OffcanvasBody className="pb-4" style={{ overflowY: "auto" }}>
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={Yup.object({
                  actionPlanTitle: Yup.string().required("This is required"),
                  actionPlanCategory: Yup.string().required("This is required"),
                  steps: Yup.array().of(
                    Yup.object({
                      actionStepType: Yup.mixed().required(
                        "Step type is required"
                      ),
                      taskType: Yup.mixed().when("actionStepType", {
                        is: v => Number(v) !== 2,
                        then: schema =>
                          schema.required("Task type is required"),
                        otherwise: schema => schema.notRequired(),
                      }),
                      emailTemplate: Yup.mixed().when("actionStepType", {
                        is: v => Number(v) === 2,
                        then: schema =>
                          schema.required("Email template is required"),
                        otherwise: schema => schema.notRequired(),
                      }),
                    })
                  ),
                })}
                onSubmit={handlePlanSubmit}
              >
                {({ values, errors, touched, handleChange, setFieldValue }) => (
                  <Form className="needs-validation">
                    <div className="ap-section-label">{props.t("Plan details")}</div>
                    <Row className="mb-3">
                      <Col>
                        <FormGroup className="mb-0">
                          <Label>{props.t("Name")} *</Label>
                          <SafeField
                            name="actionPlanTitle"
                            onChange={handleChange}
                            type="text"
                            className={
                              "form-control" +
                              (errors.actionPlanTitle && touched.actionPlanTitle
                                ? " is-invalid"
                                : "")
                            }
                          />
                          <ErrorMessage
                            name="actionPlanTitle"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup className="mb-0">
                          <Label>{props.t("Folder")} *</Label>
                          <Input
                            type="select"
                            name="actionPlanCategory"
                            value={values.actionPlanCategory}
                            onChange={handleChange}
                          >
                            {map(categories, folder => (
                              <option
                                key={folder.id ?? folder.Id}
                                value={folder.id ?? folder.Id}
                              >
                                {folder.actionPlanCategoryTitle ??
                                  folder.ActionPlanCategoryTitle}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup className="mb-0">
                          <Label>{props.t("Applies to")} *</Label>
                          <Input
                            type="select"
                            name="actionPlanType"
                            value={values.actionPlanType}
                            onChange={e => {
                              handleChange(e);
                              fetchEmailTemplates(e.target.value);
                            }}
                          >
                            {APPLIES_TO_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {props.t(opt.label)}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="ap-toggle-row form-check form-switch">
                      <input
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        id="pauseOnRespond"
                        checked={pauseOnRespond}
                        onChange={e => setPauseOnRespond(e.target.checked)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="pauseOnRespond"
                      >
                        {props.t(
                          "Automatically pause this action plan when the lead responds"
                        )}
                      </label>
                    </div>

                    <div className="ap-section-label">{props.t("Steps")}</div>
                    <FieldArray name="steps">
                      {({ push, remove }) => (
                        <>
                          {values.steps.map((step, index) => {
                            const isEmailStep =
                              Number(step.actionStepType) === 2;
                            return (
                              <Card
                                key={index}
                                className={
                                  "mb-3 ap-step-card" +
                                  (isEmailStep ? " ap-step-email" : "")
                                }
                              >
                                <CardBody>
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <strong className="d-flex align-items-center">
                                      <span className="ap-step-number">
                                        {index + 1}
                                      </span>
                                      {props.t("Step")} {index + 1}
                                    </strong>
                                    {values.steps.length > 1 && (
                                      <button
                                        type="button"
                                        className="ap-step-remove"
                                        onClick={() => remove(index)}
                                      >
                                        <i className="bx bx-trash" />
                                        {props.t("Remove")}
                                      </button>
                                    )}
                                  </div>

                                  <Row className="align-items-end">
                                    <Col sm="12" className="mb-2">
                                      <Label className="mb-1">
                                        {props.t("Runs")}{" "}
                                        <Input
                                          type="number"
                                          min={0}
                                          style={{
                                            width: 80,
                                            display: "inline-block",
                                          }}
                                          name={`steps.${index}.daysAfterPreviousTask`}
                                          value={step.daysAfterPreviousTask}
                                          onChange={handleChange}
                                        />{" "}
                                        {props.t("days after the previous step")}
                                      </Label>
                                    </Col>
                                    <Col md="4" className="mb-2">
                                      <Label className="mb-1">
                                        {props.t("Name (optional)")}
                                      </Label>
                                      <SafeField
                                        name={`steps.${index}.taskName`}
                                        onChange={handleChange}
                                        type="text"
                                        className="form-control"
                                      />
                                    </Col>
                                    <Col md="4" className="mb-2">
                                      <Label className="mb-1">
                                        {props.t("Step type")}*
                                      </Label>
                                      <Input
                                        type="select"
                                        name={`steps.${index}.actionStepType`}
                                        value={step.actionStepType}
                                        onChange={e => {
                                          const nextType = e.target.value
                                            ? parseInt(e.target.value, 10)
                                            : "";
                                          setFieldValue(
                                            `steps.${index}.actionStepType`,
                                            nextType
                                          );
                                          // Match RS-agent: automated email forces taskType email
                                          if (nextType === 2) {
                                            setFieldValue(
                                              `steps.${index}.taskType`,
                                              "1"
                                            );
                                          }
                                        }}
                                      >
                                        <option value="">
                                          {props.t("Select step type")}
                                        </option>
                                        {STEP_TYPE_OPTIONS.map(opt => (
                                          <option
                                            key={opt.value}
                                            value={opt.value}
                                          >
                                            {props.t(opt.label)}
                                          </option>
                                        ))}
                                      </Input>
                                      <ErrorMessage
                                        name={`steps.${index}.actionStepType`}
                                        component="div"
                                        className="invalid-feedback d-block"
                                      />
                                    </Col>
                                    <Col md="4" className="mb-2">
                                      <Label className="mb-1">
                                        {isEmailStep
                                          ? props.t("Select email template")
                                          : props.t("Task type")}
                                        *
                                      </Label>
                                      {isEmailStep ? (
                                        <>
                                          <Input
                                            type="select"
                                            name={`steps.${index}.emailTemplate`}
                                            value={step.emailTemplate || ""}
                                            onChange={handleChange}
                                          >
                                            <option value="">
                                              {props.t("Select email template")}
                                            </option>
                                            {emailTemplates.map(tpl => (
                                              <option
                                                key={tpl.id}
                                                value={tpl.id}
                                              >
                                                {tpl.templateTitle}
                                              </option>
                                            ))}
                                          </Input>
                                          <ErrorMessage
                                            name={`steps.${index}.emailTemplate`}
                                            component="div"
                                            className="invalid-feedback d-block"
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <Input
                                            type="select"
                                            name={`steps.${index}.taskType`}
                                            value={step.taskType}
                                            onChange={handleChange}
                                          >
                                            <option value="">
                                              {props.t("Select task type")}
                                            </option>
                                            {TASK_TYPE_OPTIONS.map(opt => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                              >
                                                {props.t(opt.label)}
                                              </option>
                                            ))}
                                          </Input>
                                          <ErrorMessage
                                            name={`steps.${index}.taskType`}
                                            component="div"
                                            className="invalid-feedback d-block"
                                          />
                                        </>
                                      )}
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            );
                          })}

                          <button
                            type="button"
                            className="ap-add-step-btn mb-4"
                            onClick={() => push(emptyStep())}
                          >
                            <i className="bx bx-plus me-1" />
                            {props.t("Add step")}
                          </button>
                        </>
                      )}
                    </FieldArray>

                    <div className="ap-offcanvas-footer">
                      <SaveButton>{props.t("Save")}</SaveButton>
                    </div>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </>
  );
};

ActionPlanList.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(ActionPlanList);
